/**
 * Guardar um marco — a folha que o Flutter não tem.
 *
 * Lá o `MilestoneService.addMilestone` existe e tela nenhuma o chama: quem
 * quisesse guardar um marco abria o console do Firebase e digitava o documento
 * à mão. É a mesma divergência de sempre — o buraco é do outro app também, e a
 * casa o fecha do lado de cá, com a mesma precedência que já valeu para os
 * outros: o dado continua idêntico, só o caminho até ele é novo.
 *
 * O formulário é o do `FolhaFoto` esticado: as fotos sobem primeiro pela API e
 * só depois o documento entra no armazém, porque um marco com endereço vazio
 * seria um cartão de vidro em branco passando no carrossel para sempre.
 *
 * Mais de uma foto de propósito. O cartão do Arquivo Vivo tem um `PageView`
 * dentro que troca de imagem de três em três segundos; com uma foto só esse
 * relógio nunca corre, e o cartão de lá foi desenhado para a tira de filme.
 */

import { useEffect, useState } from 'react';
import {
  COLECOES,
  TIPOS_DE_MARCO,
  marcoParaMapa,
  traduzir,
  usarSessao,
  type TipoDeMarco,
} from '@nosso-sempre/shared';
import { api, armazem } from '../nucleo/api';
import { Folha } from './Folha';
import { Icone } from './Icone';
import { Aviso } from './Pecas';
import './arquivoVivo.css';

/** Os mesmos tipos do `storage.rules`, como na folha da galeria. */
const TIPOS_DE_FOTO = 'image/jpeg,image/png,image/gif,image/webp,image/heic';

/** O teto do `storage.rules`. Conferir aqui poupa 8 MB de subida para nada. */
const LIMITE = 8 * 1024 * 1024;

/** Quantas fotos cabem num marco: seis dão meio minuto de volta no cartão. */
const MAXIMO = 6;

/**
 * O chão do calendário. Fica muito antes do primeiro dia do caderno (2020)
 * porque um "First Date" é justamente o que precede tudo — a data mais antiga
 * que a casa guarda é a que ela existe para lembrar.
 */
const PRIMEIRO_DIA = '1990-01-01';

/** O `YYYY-MM-DD` de hoje na hora local, que é o que o `<input type="date">` lê. */
function diaDe(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${dia}`;
}

export function FolhaMarco({ aberta, aoFechar }: { aberta: boolean; aoFechar: () => void }) {
  const { quem } = usarSessao();

  const hoje = diaDe(new Date());

  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [tipo, setTipo] = useState<TipoDeMarco>('memory');
  const [quando, setQuando] = useState(hoje);
  const [arquivos, setArquivos] = useState<File[]>([]);
  const [previas, setPrevias] = useState<string[]>([]);
  const [subindo, setSubindo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // Cada prévia é um endereço de objeto, e ele fica preso na memória até
  // alguém devolvê-lo. Trocar a escolha ou fechar a folha devolve os antigos.
  useEffect(() => {
    if (arquivos.length === 0) {
      setPrevias([]);
      return;
    }
    const urls = arquivos.map((f) => URL.createObjectURL(f));
    setPrevias(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [arquivos]);

  useEffect(() => {
    if (!aberta) {
      setTitulo('');
      setDescricao('');
      setTipo('memory');
      setQuando(diaDe(new Date()));
      setArquivos([]);
      setErro(null);
    }
  }, [aberta]);

  function escolher(lista: FileList | null) {
    setErro(null);
    if (!lista || lista.length === 0) return;
    const novos = Array.from(lista);
    if (novos.some((f) => f.size >= LIMITE)) {
      setErro(traduzir('A foto tem de ter até 8 MB.'));
      return;
    }
    setArquivos((atuais) => [...atuais, ...novos].slice(0, MAXIMO));
  }

  function tirar(i: number) {
    setArquivos((atuais) => atuais.filter((_, n) => n !== i));
  }

  async function guardar() {
    if (titulo.trim().length === 0 || subindo) return;
    setSubindo(true);
    setErro(null);
    try {
      // Em fila, e não em paralelo: são até seis fotos de 8 MB, e a API do
      // Flutter também sobe uma de cada vez.
      const imagens: string[] = [];
      for (const f of arquivos) {
        imagens.push(await api.subirFoto(await f.arrayBuffer(), f.name, f.type || 'image/jpeg'));
      }
      await armazem.adicionar(
        COLECOES.marcos,
        marcoParaMapa({
          titulo: titulo.trim(),
          descricao: descricao.trim(),
          // Lido na hora local, para o dia não voltar um.
          quando: new Date(`${quando}T00:00:00`),
          imagens,
          autor: quem ?? null,
          tipo,
        }),
      );
      aoFechar();
    } catch (e) {
      setErro(
        traduzir('Não deu para guardar o marco: {message}', { message: (e as Error).message }),
      );
    } finally {
      setSubindo(false);
    }
  }

  return (
    <Folha aberta={aberta} aoFechar={aoFechar} titulo={traduzir('Guardar um marco')} centro>
      <div className="marco-forma">
        <label className="so-leitor" htmlFor="marco-titulo">
          {traduzir('O nome do marco')}
        </label>
        <input
          id="marco-titulo"
          type="text"
          value={titulo}
          maxLength={80}
          placeholder={traduzir('O nome do marco')}
          onChange={(e) => setTitulo(e.target.value)}
        />

        <label className="so-leitor" htmlFor="marco-conta">
          {traduzir('Conte o que aconteceu')}
        </label>
        <textarea
          id="marco-conta"
          rows={3}
          value={descricao}
          placeholder={traduzir('Conte o que aconteceu')}
          onChange={(e) => setDescricao(e.target.value)}
        />

        <div className="marco-linha">
          <span className="marco-escolha">
            <label className="so-leitor" htmlFor="marco-tipo">
              {traduzir('Tipo de marco')}
            </label>
            <select
              id="marco-tipo"
              value={tipo}
              onChange={(e) => setTipo(e.target.value as TipoDeMarco)}
            >
              {TIPOS_DE_MARCO.map((t) => (
                <option key={t.chave} value={t.chave}>
                  {t.nome}
                </option>
              ))}
            </select>
            <Icone nome="expandir" tamanho={20} />
          </span>

          <label className="so-leitor" htmlFor="marco-quando">
            {traduzir('Quando aconteceu')}
          </label>
          <input
            id="marco-quando"
            type="date"
            value={quando}
            min={PRIMEIRO_DIA}
            max={hoje}
            onChange={(e) => setQuando(e.target.value)}
          />
        </div>

        <ul className="marco-fotos">
          {previas.map((url, i) => (
            <li key={url}>
              <img src={url} alt={traduzir('A foto escolhida')} />
              <button
                type="button"
                className="marco-tirar"
                onClick={() => tirar(i)}
                aria-label={traduzir('Tirar esta foto')}
              >
                <Icone nome="fechar" tamanho={14} />
              </button>
            </li>
          ))}
          {arquivos.length < MAXIMO ? (
            <li>
              <label className="marco-por">
                <input
                  type="file"
                  accept={TIPOS_DE_FOTO}
                  multiple
                  className="so-leitor"
                  onChange={(e) => {
                    escolher(e.target.files);
                    // Sem isto, escolher a mesma foto de novo depois de a
                    // tirar não dispara `change` nenhum.
                    e.target.value = '';
                  }}
                />
                <Icone nome="foto-mais" tamanho={24} />
                <span className="so-leitor">{traduzir('Toque para escolher uma foto')}</span>
              </label>
            </li>
          ) : null}
        </ul>
      </div>

      {erro ? <Aviso>{erro}</Aviso> : null}

      <div className="folha-pe">
        <button className="botao calmo" type="button" onClick={aoFechar}>
          {traduzir('Cancelar')}
        </button>
        <button
          className="botao"
          type="button"
          onClick={guardar}
          disabled={titulo.trim().length === 0 || subindo}
        >
          {subindo ? traduzir('Guardando…') : traduzir('Guardar')}
        </button>
      </div>
    </Folha>
  );
}
